// The printer half of the contract, shared by every shell and the mock, so the rules can only
// be implemented once: a 5-second timeout, `print()` never throws, failed jobs are queued.

import { type ReprintQueue } from './reprint-queue.js';
import { withTimeout } from './timeout.js';
import {
  DEVICE_TIMEOUT_MS,
  type LabelJob,
  type Printer,
  type PrinterKind,
  type PrinterState,
  type PrinterStatus,
  type PrintFailureReason,
  type PrintResult,
} from './types.js';

export type TransportOutcome =
  { ok: true } | { ok: false; reason: PrintFailureReason; message?: string };

export interface TransportStatus {
  state: PrinterState;
  target?: string;
  message?: string;
}

/** How one shell moves bytes to a printer. May reject; must stop work when `signal` aborts. */
export interface PrinterTransport {
  readonly kind: PrinterKind;
  send(job: LabelJob, signal: AbortSignal): Promise<TransportOutcome>;
  probe(signal: AbortSignal): Promise<TransportStatus>;
}

/**
 * Extra time the JS guard gives a transport that enforces `timeoutMs` itself (Node `net`,
 * Kotlin, Swift). Without it the two timers race: JS could report `timeout` and queue a
 * reprint while the transport's send was in fact succeeding, printing the label twice.
 */
export const TRANSPORT_GRACE_MS = 500;

export interface PrinterOptions {
  transport: PrinterTransport;
  queue: ReprintQueue;
  /** Default 5 s (plan §9.6). Passed to the transport, and used in the timeout message. */
  timeoutMs?: number;
  /**
   * How much longer than `timeoutMs` the JS guard waits, so a transport that applies
   * `timeoutMs` itself normally reports its own result. Use `TRANSPORT_GRACE_MS` for such
   * transports; default 0 (the guard is the only timeout).
   */
  graceMs?: number;
  now?: () => Date;
}

export function failureMessage(reason: PrintFailureReason, timeoutMs = DEVICE_TIMEOUT_MS): string {
  switch (reason) {
    case 'timeout':
      return `The printer did not answer within ${Math.round(timeoutMs / 1000)} seconds.`;
    case 'refused':
      return 'The printer refused the connection. Check it is switched on and the address is right.';
    case 'unreachable':
      return 'The printer cannot be reached on the network.';
    case 'not-configured':
      return 'No printer is set up on this station.';
    case 'unsupported':
      return 'This device cannot print labels.';
    case 'error':
      return 'The label did not print.';
  }
}

const STATE_MESSAGES: Record<PrinterState, string> = {
  ready: 'Printer ready',
  offline: 'Printer not answering',
  'not-configured': 'No printer set up',
  unsupported: 'Printing not available on this device',
};

/** A job is worth queuing unless the device can never print it. */
const queueable = (reason: PrintFailureReason) => reason !== 'unsupported';

export function createPrinter({
  transport,
  queue,
  timeoutMs = DEVICE_TIMEOUT_MS,
  graceMs = 0,
  now = () => new Date(),
}: PrinterOptions): Printer {
  const guardMs = timeoutMs + Math.max(0, graceMs);
  return {
    kind: transport.kind,

    async print(job) {
      const started = Date.now();
      let outcome: TransportOutcome;
      try {
        outcome = await withTimeout(
          (signal) => transport.send(job, signal),
          guardMs,
          (): TransportOutcome => ({ ok: false, reason: 'timeout' }),
        );
      } catch (error) {
        outcome = {
          ok: false,
          reason: 'error',
          message: error instanceof Error ? error.message : undefined,
        };
      }
      const durationMs = Date.now() - started;
      if (outcome.ok) {
        queue.remove(job.id);
        return { ok: true, jobId: job.id, printer: transport.kind, durationMs };
      }
      const message = outcome.message ?? failureMessage(outcome.reason, timeoutMs);
      const queued = queueable(outcome.reason);
      if (queued) queue.add(job, outcome.reason, message, now().toISOString());
      const result: PrintResult = {
        ok: false,
        jobId: job.id,
        printer: transport.kind,
        reason: outcome.reason,
        message,
        queued,
        durationMs,
      };
      return result;
    },

    async status() {
      let status: TransportStatus;
      try {
        status = await withTimeout(
          (signal) => transport.probe(signal),
          guardMs,
          (): TransportStatus => ({
            state: 'offline',
            message: failureMessage('timeout', timeoutMs),
          }),
        );
      } catch (error) {
        status = {
          state: 'offline',
          message: error instanceof Error ? error.message : STATE_MESSAGES.offline,
        };
      }
      const result: PrinterStatus = {
        printer: transport.kind,
        state: status.state,
        message: status.message ?? STATE_MESSAGES[status.state],
        checkedAt: now().toISOString(),
      };
      if (status.target !== undefined) result.target = status.target;
      return result;
    },
  };
}
